<script setup>
import { ref, watch, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import MessagingView from './MessagingView.vue';
import {useAssistantPanel} from '~/composables/useAssistantPanel';
import {useAuthService} from '~/composables/useAuthService';

const route = useRoute();
const router = useRouter();

// Dialog starts closed for SSR (prevents classList error)
const isOpen = ref(false);

// After mounting on client, check query param and open if needed
onMounted(() => {
  if (route.query.messaging === 'open') {
    isOpen.value = true;
  }
});

// Watch for query param changes (after mount)
watch(() => route.query.messaging, (newVal) => {
  isOpen.value = newVal === 'open';
});

// When dialog state changes, update URL accordingly
watch(isOpen, (newVal) => {
  if (!newVal && route.query.messaging === 'open') {
    // Dialog closed - remove messaging query params
    const { messaging, conversationId, ...rest } = route.query;
    router.push({ query: rest });
  } else if (newVal && route.query.messaging !== 'open') {
    // Dialog opened - add query param
    router.push({
      query: { ...route.query, messaging: 'open' }
    });
  }
});

const handleClose = () => {
  isOpen.value = false;
};

// Open the assistant popup over the messaging overlay (non-modal, so messaging
// stays put behind it).
const {open: openAssistant} = useAssistantPanel();
const {canUseAssistant} = useAuthService();
const handleOpenAssistant = () => openAssistant();
</script>

<template>
  <v-dialog
    v-model="isOpen"
    fullscreen
    transition="dialog-bottom-transition"
    :scrim="false"
  >
    <div class="msg-overlay">
      <!-- dialog toolbar (mockup .msg-head) -->
      <div class="msg-head">
        <button type="button" class="msg-iconbtn" aria-label="Close messages" @click="handleClose">
          <v-icon size="21">mdi-close</v-icon>
        </button>
        <div class="msg-title">Messages</div>
        <v-spacer/>
        <!-- Grant 2026-11-02: Tappy, the help chat, with its chat-question icon. -->
        <button v-if="canUseAssistant" type="button" class="msg-iconbtn" aria-label="Ask Tappy" @click="handleOpenAssistant">
          <v-icon size="21">mdi-chat-question-outline</v-icon>
          <v-tooltip activator="parent" location="bottom">Ask Tappy</v-tooltip>
        </button>
      </div>

      <MessagingView class="msg-body" />
    </div>
  </v-dialog>
</template>

<style scoped lang="scss">
.msg-overlay {
  position: absolute;
  inset: 0;
  background: rgb(var(--v-theme-surface));
  display: flex;
  flex-direction: column;
}

.msg-head {
  height: 56px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 12px;
  border-bottom: 1px solid rgb(var(--v-theme-borderLight));
}

.msg-iconbtn {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgb(var(--v-theme-borderLight));
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgb(var(--v-theme-text));

  &:hover {
    background: rgb(var(--v-theme-light));
  }
}

.msg-title {
  font-size: 15.5px;
  font-weight: 700;
  letter-spacing: -0.2px;
}

.msg-body {
  flex: 1;
  min-height: 0;
}
</style>
